import React from 'react';
import { Link } from 'react-router-dom';
import { openCookiePreferences } from '@/components/CookieConsent';
import { Phone, Mail, MapPin, Facebook, Linkedin, Clock } from 'lucide-react';
import { treatmentAreas } from '@/data/treatmentAreas';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_handcare-redesign/artifacts/75wjnq72_Untitled-design.webp";

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'Treatment Areas', href: '/treatment-areas' },
  { label: 'Doctors', href: '/doctors' },
  { label: 'Areas We Serve', href: '/areas-we-serve' },
  { label: 'Patient Resources', href: '/patient-resources' },
  { label: 'Contact Us', href: '/contact' },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-foreground text-background">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img 
              src={LOGO_URL} 
              alt="Melbourne Hand Center" 
              className="h-12 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-background/70 mb-6">
              Academic quality hand care in a community setting. Fellowship-trained 
              specialists dedicated to your hand health.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/Melbourne-Hand-Center-Dr-Kyle-Moyles-1468070256836259/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-background/10 flex items-center justify-center hover:bg-primary transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="#"
                className="w-10 h-10 rounded-lg bg-background/10 flex items-center justify-center hover:bg-primary transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-semibold text-background mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-background/70 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Treatment Areas */}
          <div>
            <h4 className="font-display text-lg font-semibold text-background mb-6">
              Treatment Areas
            </h4>
            <ul className="space-y-3">
              {treatmentAreas.slice(0, 6).map((treatment) => (
                <li key={treatment.slug}>
                  <Link 
                    to={`/treatment-areas/${treatment.slug}`}
                    className="text-background/70 hover:text-primary transition-colors"
                  >
                    {treatment.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display text-lg font-semibold text-background mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <div className="text-background/70">
                  <p>2010 W. Eau Gallie Blvd</p>
                  <p>Suite 104</p>
                  <p>Melbourne, FL 32935</p>
                </div>
              </li>
              <li>
                <a 
                  href="tel:321-500-4263" 
                  className="flex items-center gap-3 text-background/70 hover:text-primary transition-colors"
                >
                  <Phone className="w-5 h-5 text-primary" />
                  321.500.HAND (4263)
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@melbournehandcenter.com"
                  className="flex items-center gap-3 text-background/70 hover:text-primary transition-colors"
                >
                  <Mail className="w-5 h-5 text-primary" />
                  info@melbournehandcenter.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary mt-0.5" />
                <div className="text-background/70">
                  <p>Monday - Friday</p>
                  <p>8:00 AM - 5:00 PM</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-background/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-background/60 text-sm">
              © {currentYear} Melbourne Hand Center. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <Link to="/accessibility" className="text-background/60 hover:text-primary transition-colors">
                Accessibility
              </Link>
              <button
                type="button"
                onClick={openCookiePreferences}
                data-testid="footer-cookie-preferences"
                className="text-background/60 hover:text-primary transition-colors"
              >
                Cookie Preferences
              </button>
              <a href="#" className="text-background/60 hover:text-primary transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-background/60 hover:text-primary transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-background/60 hover:text-primary transition-colors">
                HIPAA Notice
              </a>
            </div>
          </div>
          <div className="text-center mt-4 pt-4 border-t border-background/10">
            <p className="text-background/50 text-sm">
              Website Designed By{' '}
              <a 
                href="https://bp.agency" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Brand Partners
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
